<template>
  <div class="itemslist">
    <slot />
    <Ordering
      :list-key="listKey"
      :ids="displayedTaskIds"
      :disabled="!!isEditing"
      :reorderable="!!listKey"
      :managed="false"
      @reorder="onReorder"
    >
      <template #item="{ id }">
        <div class="task-row">
          <Suspense>
            <RecurrentTaskInline
              v-if="isRecurrent"
              :recurrent-task-id="id"
              :is-editing="!!isEditing"
              :is-selected="id === selectedTaskId"
              @quit-editing="$emit('quitEditing')"
              @select="$emit('taskClick', id)"
              @set-selected-task-el="(el) => $emit('setSelectedTaskEl', el)"
            />
            <TaskInline
              v-else
              :actions="actions"
              :class="{
                selected: id === selectedTaskId,
              }"
              :is-editing="isEditing"
              :is-selected="id === selectedTaskId"
              :reorderable="!!listKey"
              :task-id="id"
              @quit-editing="$emit('quitEditing')"
              @select="$emit('taskClick', id)"
              @set-selected-task-el="(el) => $emit('setSelectedTaskEl', el)"
            />
            <template #fallback>
              <div
                class="task-skeleton"
                :class="{ 'has-reorder-column': !!listKey }"
              >
                <BaseSkeletonLine is-compact />
              </div>
            </template>
          </Suspense>
        </div>
      </template>
      <template v-if="showLoadMore" #footer>
        <div
          ref="infiniteScrollTrigger"
          class="infinite-scroll-trigger"
          :class="{ loading: isLoadingMore }"
        >
          <div v-if="isLoadingMore" class="loading-more">
            <Spinner />
            <span>Loading more tasks...</span>
          </div>
        </div>
      </template>
    </Ordering>
  </div>
</template>
<script lang="ts">
import { debounce } from "lodash-es";
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";

import BaseSkeletonLine from "@/components/base/BaseSkeletonLine.vue";
import RecurrentTaskInline from "@/components/RecurrentTaskInline.vue";
import TaskInline from "@/components/TaskInline.vue";
import { useOrderableTaskList } from "@/components/tasks/ordering";
import Ordering from "@/components/tasks/Ordering.vue";
import { useReleaser } from "@/ref";

import { defaultTaskActions, TaskActions } from "../task/types";

export default defineComponent({
  components: { BaseSkeletonLine, Ordering, RecurrentTaskInline, TaskInline },
  props: {
    actions: {
      type: Object as PropType<TaskActions>,
      required: false,
      default: defaultTaskActions,
    },
    isEditing: { type: Boolean, required: true },
    isRecurrent: { type: Boolean, required: false, default: false },
    selectedTaskId: {
      type: [String, null] as PropType<string | null>,
      required: false,
    },
    taskIds: { type: Array as PropType<string[]>, required: true },
    /** Shared ranking key; omit for controller order only. */
    listKey: { type: String, required: false, default: undefined },
    pagination: { type: Number, required: false, default: undefined },
  },
  emits: {
    quitEditing: () => true,
    setSelectedTaskEl: (_: HTMLElement) => true,
    taskClick: (_: string) => true,
  },
  setup(props) {
    const { taskIds, listKey, pagination } = toRefs(props);

    const listKeyRef = computed(() => listKey.value);
    const {
      orderedIdsReadonly,
      commitOrder,
      release: releaseOrdering,
    } = useOrderableTaskList(listKeyRef, taskIds);

    const orderedTaskIds = computed(() =>
      listKey.value ? orderedIdsReadonly.value : taskIds.value
    );

    // Pagination state
    const currentPage = ref(1);
    const isLoadingMore = ref(false);
    const infiniteScrollTrigger = ref<HTMLElement | null>(null);

    const displayedTaskIds = computed(() => {
      if (pagination.value === undefined) {
        return orderedTaskIds.value;
      }
      const itemsToShow = currentPage.value * pagination.value;
      return orderedTaskIds.value.slice(0, itemsToShow);
    });

    const onReorder = (visibleAfter: string[]) => {
      if (listKey.value) commitOrder(visibleAfter);
    };

    const showLoadMore = computed(() => {
      return (
        pagination.value !== undefined &&
        displayedTaskIds.value.length < orderedTaskIds.value.length
      );
    });

    const remainingCount = computed(() => {
      return orderedTaskIds.value.length - displayedTaskIds.value.length;
    });

    // Smart pagination reset - only reset when list significantly shrinks
    const previousLength = ref(taskIds.value.length);
    watch(taskIds, (newTaskIds, oldTaskIds) => {
      const newLength = newTaskIds.length;
      const oldLength = oldTaskIds?.length || 0;

      // Only reset pagination if:
      // 1. List became significantly smaller (more than half) - indicates filter change
      // 2. List became empty
      // 3. This is the first load (oldLength is 0)
      if (newLength === 0 || oldLength === 0 || newLength < oldLength * 0.5) {
        currentPage.value = 1;
        isLoadingMore.value = false;
      }

      previousLength.value = newLength;
    });

    const loadMore = async () => {
      if (isLoadingMore.value || !showLoadMore.value) return;

      isLoadingMore.value = true;
      // Simulate loading delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 300));
      currentPage.value++;
      isLoadingMore.value = false;
    };

    const releaser = useReleaser();
    releaser.defer(releaseOrdering);

    const loadMoreDebounced = debounce(async () => {
      if (!isLoadingMore.value) {
        await loadMore();
      }
    }, 300);
    releaser.defer(() => loadMoreDebounced.cancel());

    // Observe the sentinel whenever it exists (it is v-if'd, so it may
    // appear after mount when the list grows past one page).
    watch(
      infiniteScrollTrigger,
      (el, _prev, onCleanup) => {
        if (!el || pagination.value === undefined) return;
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry?.isIntersecting) loadMoreDebounced();
          },
          {
            rootMargin: "80px",
            threshold: 0,
          }
        );
        observer.observe(el);
        onCleanup(() => observer.disconnect());
      },
      { flush: "post" }
    );

    return {
      displayedTaskIds,
      onReorder,
      showLoadMore,
      remainingCount,
      isLoadingMore,
      loadMore,
      infiniteScrollTrigger,
    };
  },
});
</script>
<style scoped lang="scss">
@use "@/styles/itemslist.scss";

.task-row {
  width: 100%;
}

.task-skeleton {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 42px;
  padding: 6px 16px;
  box-sizing: border-box;

  // Match TaskInline reorderable: handle column before the checkbox
  // (padding-start 8 + handle 16 + margin-end 8 = 32px).
  &.has-reorder-column {
    padding-inline-start: 32px;
  }

  :deep(.base-skeleton-line) {
    flex: 1;
    min-width: 0;
    width: 100%;
  }
}

.infinite-scroll-trigger {
  height: 1px;
  overflow: hidden;

  &.loading {
    height: auto;
    overflow: visible;
    min-height: 40px;
    margin: 8px 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .loading-more {
    display: flex;
    align-items: center;
    gap: 8px;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
